import { useEffect, useMemo } from 'react';
import { useTranslation } from '@jupiter/plugin-runtime/i18n';
import { useHistory } from 'react-router-dom';
import { getQuery } from '@i18n-ecom/lib/dist/commonjs/url';
import { CODE_SUCCESS } from '@features/constants/network';
import { LearningInfo } from '@/api/seller/serv/oec_seller_learning_center_api';
import { genLocationByLearningInfo } from '../location';
import { basename, ROLE_KEY, RoleType, RoleTypeMap } from '@/constants';
import { useUserModel } from '@/models';
import { usePolicyModel } from './models';
import { REGION_CONFIG } from '@/region-config';
import { Tea } from '@features/libs/tea';
import { getPopReportQuery, usePopQuery } from '@/hooks/usePopQuery';

export const usePolicy = () => {
  const { i18n } = useTranslation();
  const history = useHistory();
  const { location } = history;
  const { roleType: oriRoleType } = useUserModel(({ roleType }) => [roleType]);
  const {
    menu,
    clearMenuCache,
    latestPolicy,
    policyCourses,
    init,
    loadLatestPolicy,
    loadPolicyCourses,
  } = usePolicyModel();
  const { region } = usePopQuery();
  const roleType = useMemo(() => {
    return RoleTypeMap[
      getQuery(location.search, ROLE_KEY)?.toString() ||
        oriRoleType ||
        RoleType.Seller
    ];
  }, []);

  useEffect(() => {
    if (region) {
      clearMenuCache();
    }
  }, [region]);

  const wait = async () => {
    const res = await init(roleType);

    loadLatestPolicy();
    !REGION_CONFIG.notNeedCourse && loadPolicyCourses();

    // lazyload
    setTimeout(() => {
      handleChange(
        roleType === RoleType.Seller ? RoleType.Creator : RoleType.Seller,
      );
    }, 300);

    return res?.code === CODE_SUCCESS;
  };
  const handleChange = (type: RoleType) => {
    if (!menu[type]?.length) {
      init(type);
    }
  };
  const handleItemClick = (
    info: LearningInfo,
    tabName: string,
    index?: number,
    pageNumber?: number,
  ) => {
    const nextLocation = genLocationByLearningInfo(
      history.location.search,
      info,
    );

    window.open(
      `/${basename}${nextLocation.pathname}?${nextLocation.search}&from=policy`,
      '_blank',
    );

    Tea('button_click', {
      page_name: 'seller_university_homepage',
      module_name: 'seller_university_homepage_policy',
      tab_name: tabName,
      click_for: 'seller_university_content_list_item',
      content_id: info.learning_id,
      content_name: info.name,
      index: index ? index + 1 : index,
      page_num: pageNumber,
      ...getPopReportQuery(),
    });
  };

  useEffect(() => {
    init(roleType);
    loadLatestPolicy();
    !REGION_CONFIG.notNeedCourse && loadPolicyCourses();
  }, [i18n.language]);

  return {
    latestPolicy,
    policyCourses,
    wait,
    handleChange,
    handleItemClick,
  };
};
